"use client";

import { Icon } from "@repo/dashboard-core";
import { useTranslations } from "next-intl";
import { QRCodeSVG } from "qrcode.react";

import { CopyableValue } from "@/components/topup/CopyableValue";
import type { EsimProfile } from "@/lib/types/esim";
import { formatIccid, manualInstallDetails } from "@/lib/utils/esim";

type InstallLink = {
  href: string | null;
  label: string;
  icon: "share" | "download";
};

/**
 * Everything needed to get the profile onto a phone.
 *
 * The QR encodes the LPA activation code, which is a bearer credential —
 * whoever installs the profile first consumes the data — so this panel is only
 * ever rendered for an eSIM the signed-in customer owns.
 */
export function EsimInstall({ profile }: { profile: EsimProfile }) {
  const t = useTranslations("esim");
  const { smdpAddress, activationCode } = manualInstallDetails(profile);

  const links: InstallLink[] = [
    {
      href: profile.appleUniversalLink,
      label: t("install-apple"),
      icon: "share",
    },
    {
      href: profile.androidUniversalLink,
      label: t("install-android"),
      icon: "download",
    },
  ];

  const available = links.filter((link) => link.href);

  return (
    <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
      <div className="mx-auto shrink-0 rounded-2xl bg-pureWhite p-4 shadow-shell-sm sm:mx-0">
        <QRCodeSVG
          value={profile.activationCode}
          size={176}
          level="M"
          marginSize={0}
          bgColor="#FFFFFF"
          fgColor="#000000"
          title={t("install-qr-title")}
        />
      </div>

      <div className="min-w-0 flex-1 space-y-5">
        <p className="text-base text-text-secondary">
          {t("install-scan-hint")}
        </p>

        <dl className="space-y-3">
          <InstallField
            label={t("field-iccid")}
            value={formatIccid(profile.iccid)}
            copy={profile.iccid}
          />
          {smdpAddress && (
            <InstallField
              label={t("field-smdp")}
              value={smdpAddress}
              copy={smdpAddress}
            />
          )}
          <InstallField
            label={t("field-activation-code")}
            value={activationCode}
            copy={activationCode}
          />
        </dl>

        {available.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {available.map((link) => (
              <a
                key={link.label}
                href={link.href ?? "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-surface-border px-3 py-2 text-base font-medium text-text-primary transition-colors hover:border-primary hover:text-primary"
              >
                <Icon name={link.icon} size={16} />
                {link.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InstallField({
  label,
  value,
  copy,
}: {
  label: string;
  value: string;
  copy: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-sm font-medium uppercase tracking-wide text-text-muted">
        {label}
      </dt>
      <dd className="mt-0.5">
        <CopyableValue
          value={value}
          copyValue={copy}
          className="max-w-full break-all text-left font-mono text-base text-text-primary"
        />
      </dd>
    </div>
  );
}
